// Lobby.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TableCard from "@/components/TableCard";
import AttendeeProfileModal from "@/components/AttendeeProfileModal/AttendeeProfileModal";
import WelcomeVideoOverlay from "@/components/WelcomeVideoOverlay/WelcomeVideoOverlay";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Filter, Calendar, X, ArrowRight } from "lucide-react";

import {
  getEventById,
  getAllEvents,
  bookEvent,
  getAllEventSessionsTables,
} from "@/Services/EventServices";
import {
  getSessionTables,
  getTableParticipants,
  joinSessionTable,
  leaveSessionTable,
} from "@/Services/EventMeetingServices";

// ---------------------------------------------------------------------------
// CONSTANTS — module-scope, never recreated
// ---------------------------------------------------------------------------

const ALL_FILTER = "All";
const POLL_INTERVAL_MS = 10000;

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — module-scope
// ---------------------------------------------------------------------------

const gmtLabelToOffsetMinutes = (tz) => {
  const s = String(tz || "")
    .replace(/[()]/g, "")
    .trim();
  const m = s.match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2] || 0) * 60 + Number(m[3] || 0));
};

const isoFromParts = (dateStr, timeStr, tzLabel) => {
  if (!dateStr || !timeStr) return "";
  const mins = gmtLabelToOffsetMinutes(tzLabel);
  if (mins == null) return `${dateStr}T${timeStr}:00Z`;
  const sign = mins < 0 ? "-" : "+";
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${dateStr}T${timeStr}:00${sign}${hh}:${mm}`;
};

const stripHtml = (html) =>
  html
    ? String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const fmtShortDate = (iso, tzLabel) => {
  try {
    if (!iso) return "TBA";
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "TBA";
    const opts = {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const offsetMins = gmtLabelToOffsetMinutes(tzLabel);
    if (offsetMins != null) {
      return new Date(d.getTime() + offsetMins * 60_000).toLocaleDateString(
        undefined,
        { ...opts, timeZone: "UTC" },
      );
    }
    return d.toLocaleDateString(undefined, {
      ...opts,
      timeZone: tzLabel || undefined,
    });
  } catch {
    return "TBA";
  }
};

const initialsFor = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ---------------------------------------------------------------------------
// DATA NORMALIZERS — pure, module-scope
// ---------------------------------------------------------------------------

const normalizeTable = (t, eventId, sessionId, participants) => {
  const sponsor = t?.sponsorDetails || t?.sponcer || null;
  return {
    id: t._id,
    event_id: eventId,
    session_id: sessionId,
    circle: sponsor?.title || "Tables",
    topic: (t.tableName || `Table ${t.tableNumber || ""}`).trim(),
    description: stripHtml(t.agenda || ""),
    seats: Number(t.capacity ?? 8),
    taken: Number(t.currentOccupancy ?? 0),
    is_live: !!t.isActive,
    tableIcon: sponsor?.image || "",
    attendees: (participants || []).map((p) => {
      const name = (
        p?.name ||
        p?.userName ||
        p?.fullName ||
        `${p?.firstName || ""} ${p?.lastName || ""}`.trim() ||
        "Guest"
      ).trim();
      return { name, avatar: p?.image || "", initials: initialsFor(name) };
    }),
    welcome_video_url: sponsor?.introVideo || "",
    sponsors: sponsor
      ? [
          {
            id: sponsor._id,
            company: sponsor.title,
            image: sponsor.image,
            sponsorData: sponsor,
          },
        ]
      : [],
    __raw: t,
  };
};

const flattenGlobalTables = (data) => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((item) => {
    const session = item.session;
    return (item.tables || []).map((t) => ({
      ...normalizeTable(t, t.eventId, t.sessionId, t.participants),
      session_name: session?.name,
      isUserInTable: !!t.isUserInTable,
    }));
  });
};

const normalizeUpcomingEvent = (e) => ({
  id: e._id || e.id,
  title: e.eventName || e.title,
  start_time:
    e.start_time || isoFromParts(e.startDate, e.startTime, e.timezone),
  timezone: e.timezone || "(GMT+0)",
  description: stripHtml(e.description),
});

// ---------------------------------------------------------------------------
// SKELETON — prevents CLS during load
// ---------------------------------------------------------------------------

const TableCardSkeleton = memo(() => (
  <div
    aria-hidden="true"
    className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 animate-pulse"
  >
    <div className="h-5 w-2/3 bg-white/[0.06] rounded mb-3" />
    <div className="h-3 w-full bg-white/[0.04] rounded mb-2" />
    <div className="h-3 w-4/5 bg-white/[0.04] rounded mb-6" />
    <div className="flex gap-2">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="w-7 h-7 rounded-full bg-white/[0.06]" />
      ))}
    </div>
  </div>
));
TableCardSkeleton.displayName = "TableCardSkeleton";

// ---------------------------------------------------------------------------
// SHARED MODAL SHELL
// ---------------------------------------------------------------------------

const ModalShell = memo(({ onClose, testId, maxWidth = "520px", children }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className={`w-[min(96vw,${maxWidth})] rounded-3xl border border-white/15 bg-[#0A1D1A] p-7 shadow-2xl`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
));
ModalShell.displayName = "ModalShell";

// ---------------------------------------------------------------------------
// AGENDA MODAL — only mounts when open
// ---------------------------------------------------------------------------

const AgendaModal = memo(({ open, onClose, table }) => {
  if (!open || !table) return null;
  return (
    <ModalShell onClose={onClose} testId="agenda-modal" maxWidth="600px">
      <div
        className="max-h-[calc(80vh-3.5rem)] overflow-y-auto"
        // extra inner scroll wrapper keeps the panel border-radius intact
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline mb-2">Table Agenda</p>
            <h3 className="font-display text-2xl tracking-tight mb-2">
              {table.topic || "Table Details"}
            </h3>
            {table.circle && (
              <p className="text-sm text-[#C6A87C]">{table.circle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            aria-label="Close agenda"
          >
            <X size={20} className="text-white/70" />
          </button>
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-semibold text-white/90 mb-3">Agenda</h4>
          <div className="text-white/75 leading-relaxed whitespace-pre-wrap">
            {table.description || "No agenda details available."}
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onClose}
          className="btn-gold flex-1 justify-center"
          type="button"
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
});
AgendaModal.displayName = "AgendaModal";

// ---------------------------------------------------------------------------
// REGISTER MODAL — only mounts when open
// ---------------------------------------------------------------------------

const RegisterModal = memo(
  ({ open, onClose, eventId, eventTitle, user, onBooked }) => {
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      if (open) setAgree(false);
    }, [open]);

    const submit = useCallback(async () => {
      if (!agree) {
        toast.error("Please agree to the Terms & Conditions.");
        return;
      }
      setBusy(true);
      try {
        const res = await bookEvent({
          newsId: eventId,
          agreeTerms: true,
          userName: user?.name,
          firstName: user?.name?.split(" ")?.[0],
          lastName: user?.name?.split(" ")?.slice(1)?.join(" "),
          email: user?.email,
        });
        const ticket = res?.booking?.ticketNumber || "";
        if (!ticket && !/booked/i.test(String(res?.message || ""))) {
          toast.error(res?.message || "Could not register.");
          return;
        }
        toast.success("Event booked successfully");
        onClose?.();
        onBooked?.(ticket);
      } catch {
        toast.error("Could not register.");
      } finally {
        setBusy(false);
      }
    }, [agree, eventId, user, onClose, onBooked]);

    if (!open) return null;

    return (
      <ModalShell onClose={onClose} testId="lobby-register-modal">
        <p className="overline mb-2">Register</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">
          {eventTitle || "Event"}
        </h3>

        <label className="flex items-start gap-3 mt-4 cursor-pointer">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-white/70">
            I agree to the Terms & Conditions and Privacy Policy.
          </span>
        </label>

        <div className="flex gap-3 mt-6">
          <button
            onClick={submit}
            className="btn-gold flex-1 justify-center"
            type="button"
            disabled={!agree || busy}
          >
            {busy ? "Registering…" : "Confirm registration"}{" "}
            <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="btn-ghost"
            type="button"
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </ModalShell>
    );
  },
);
RegisterModal.displayName = "RegisterModal";

// ---------------------------------------------------------------------------
// FILTER BAR — isolated so filter changes don't re-render the table grid
// ---------------------------------------------------------------------------

const FilterBar = memo(
  ({ filters, activeCircle, onCircle, onlyOpen, onToggleOpen }) => (
    <div className="mt-12 flex items-center gap-3 flex-wrap">
      <Filter size={14} className="text-white/55" aria-hidden="true" />

      <div role="group" aria-label="Filter by circle">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => onCircle(f)}
            aria-pressed={activeCircle === f}
            className={`mr-2 mb-2 px-4 py-2 rounded-full border text-xs transition-colors ${
              activeCircle === f
                ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]"
                : "border-white/[0.12] text-white/75 hover:border-white/30"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span className="text-xs text-white/65">Show:</span>
        <button
          type="button"
          onClick={() => onToggleOpen(false)}
          aria-pressed={!onlyOpen}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            !onlyOpen ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]" : "border-white/50"
          }`}
        >
          All tables
        </button>
        <button
          type="button"
          onClick={() => onToggleOpen(true)}
          aria-pressed={onlyOpen}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            onlyOpen
              ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]"
              : "border-white/50"
          }`}
        >
          Seats available
        </button>
      </div>
    </div>
  ),
);
FilterBar.displayName = "FilterBar";

// ---------------------------------------------------------------------------
// UPCOMING EVENTS STRIP — memoised, static after first load
// ---------------------------------------------------------------------------

const UpcomingStrip = memo(({ upcoming }) => {
  if (upcoming.length === 0) return null;
  return (
    <div className="mt-10 border-t border-white/[0.06] pt-8">
      <p className="overline mb-5 flex items-center gap-2">
        <Calendar size={12} aria-hidden="true" /> Upcoming events
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {upcoming.slice(0, 3).map((e) => (
          <Link
            key={e.id}
            to={`/events/${e.id}`}
            className="block border border-white/[0.08] hover:border-[#C6A87C]/40 rounded-xl p-4 transition-colors group"
          >
            <p className="text-xs text-[#C6A87C] mb-1.5">
              <Calendar size={11} className="inline mr-1" aria-hidden="true" />
              {fmtShortDate(e.start_time, e.timezone)}
            </p>
            <h3 className="font-display text-lg tracking-tight group-hover:text-[#C6A87C] transition-colors">
              {e.title}
            </h3>
            <p className="text-xs text-white/55 mt-2 line-clamp-2">
              {e.description}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
});
UpcomingStrip.displayName = "UpcomingStrip";

// ---------------------------------------------------------------------------
// JOINED TABLE BANNER — memoised
// ---------------------------------------------------------------------------

const JoinedBanner = memo(({ joinedTable, onLeave }) => {
  if (!joinedTable) return null;
  return (
    <div className="border-b border-[#C6A87C]/30 bg-[#C6A87C]/10">
      <div className="container-x py-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm">
          You're at:{" "}
          <span className="italic font-display text-lg">
            {joinedTable.topic}
          </span>
        </p>
        <button
          type="button"
          onClick={onLeave}
          className="text-xs text-[#C6A87C] hover:text-[#D9BC93] transition-colors"
        >
          Leave & browse
        </button>
      </div>
    </div>
  );
});
JoinedBanner.displayName = "JoinedBanner";

// ---------------------------------------------------------------------------
// LOBBY HERO — memoised; only changes when tables load or filters applied
// ---------------------------------------------------------------------------

const LobbyHero = memo(
  ({
    isGlobal,
    tables,
    filters,
    activeCircle,
    onCircle,
    onlyOpen,
    onToggleOpen,
    upcoming,
  }) => {
    // Aggregate stats computed once here, not in the parent
    const totalTaken = useMemo(
      () => tables.reduce((s, t) => s + (t.taken || 0), 0),
      [tables],
    );
    const totalSeats = useMemo(
      () => tables.reduce((s, t) => s + (t.seats || 0), 0),
      [tables],
    );

    return (
      <section
        className="border-b border-white/[0.06]"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x py-16 md:py-24">
          <p className="overline mb-5">The lobby · live now</p>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <h1 className="font-display font-light text-5xl md:text-7xl tracking-tighter leading-[0.95]">
                {isGlobal ? (
                  <>
                    {tables.length} tables active now
                    <br />
                    <span className="italic text-[#C6A87C]">
                      across the whole society.
                    </span>
                  </>
                ) : (
                  <>
                    {tables.length} live tables ·
                    <br />
                    <span className="italic text-[#C6A87C]">
                      {totalTaken} of {totalSeats} seats filled.
                    </span>
                  </>
                )}
              </h1>
            </div>
            <div className="md:col-span-5">
              <p className="text-white/75 leading-relaxed">
                {isGlobal
                  ? "Members are currently active across multiple events. Browse the topics below and join any table with an open seat."
                  : "Pull up a chair, leave when the conversation runs its course. Switch tables freely."}
              </p>
            </div>
          </div>

          <FilterBar
            filters={filters}
            activeCircle={activeCircle}
            onCircle={onCircle}
            onlyOpen={onlyOpen}
            onToggleOpen={onToggleOpen}
          />

          <UpcomingStrip upcoming={upcoming} />
        </div>
      </section>
    );
  },
);
LobbyHero.displayName = "LobbyHero";

// ---------------------------------------------------------------------------
// TABLE GRID — isolated so hero/filter changes don't reflow table cards
// ---------------------------------------------------------------------------

const TableGrid = memo(
  ({
    tables,
    loading,
    event,
    joinedId,
    onJoin,
    onSwitch,
    onAttendeeClick,
    onViewAgenda,
  }) => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }, (_, i) => (
            <TableCardSkeleton key={i} />
          ))}
        </div>
      );
    }

    if (tables.length === 0) {
      return (
        <div className="py-24 text-center">
          <p className="text-white/55 text-lg">No tables match your filters.</p>
          <p className="text-white/35 text-sm mt-2">
            Try removing a filter or check back when an event goes live.
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tables.map((t) => (
          <TableCard
            key={t.id}
            table={t}
            event={event}
            joined={joinedId === t.id}
            onJoin={() => onJoin(t)}
            onSwitch={onSwitch}
            onAttendeeClick={onAttendeeClick}
            onViewAgenda={() => onViewAgenda(t)}
            sponsors={t.sponsors || []}
          />
        ))}
      </div>
    );
  },
);
TableGrid.displayName = "TableGrid";

// ---------------------------------------------------------------------------
// MAIN LOBBY PAGE
// ---------------------------------------------------------------------------

export default function Lobby() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL params — read once; stable refs since searchParams only changes on navigation
  const eventId = searchParams.get("event");
  const sessionFromUrl = searchParams.get("session");

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [event, setEvent] = useState(null);
  const [eventRaw, setEventRaw] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);

  const [upcoming, setUpcoming] = useState([]);
  const [isGlobal, setIsGlobal] = useState(!eventId);

  // Filter state
  const [activeCircle, setActiveCircle] = useState(ALL_FILTER);
  const [onlyOpen, setOnlyOpen] = useState(false);

  // Interaction state
  const [joinedId, setJoinedId] = useState(null);
  const [profileFor, setProfileFor] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [agendaTable, setAgendaTable] = useState(null);

  // Abort refs — one per async effect so we never setState after unmount
  const upcomingAbort = useRef(null);
  const eventAbort = useRef(null);
  const lobbyAbort = useRef(null);
  const pollRef = useRef(null);
  const isPollRunning = useRef(false);

  const isBooked = !!eventRaw?.isBooked;

  // ---------------------------------------------------------------------------
  // DERIVED: circles list from current tables
  // ---------------------------------------------------------------------------
  const circles = useMemo(
    () => Array.from(new Set(tables.map((t) => t.circle))).filter(Boolean),
    [tables],
  );

  const filters = useMemo(() => [ALL_FILTER, ...circles], [circles]);

  // ---------------------------------------------------------------------------
  // DERIVED: visible tables after filter — runs only when deps change
  // ---------------------------------------------------------------------------
  const visible = useMemo(() => {
    let v =
      activeCircle === ALL_FILTER
        ? tables
        : tables.filter((t) => t.circle === activeCircle);
    if (onlyOpen) v = v.filter((t) => (t.seats || 0) - (t.taken || 0) > 0);
    return v;
  }, [tables, activeCircle, onlyOpen]);

  // ---------------------------------------------------------------------------
  // DERIVED: joined table object — avoids two `tables.find()` calls in JSX
  // ---------------------------------------------------------------------------
  const joinedTable = useMemo(
    () => (joinedId ? (tables.find((t) => t.id === joinedId) ?? null) : null),
    [tables, joinedId],
  );

  // ---------------------------------------------------------------------------
  // FETCH: upcoming events — fires once on mount
  // ---------------------------------------------------------------------------
  const loadUpcoming = useCallback(async () => {
    upcomingAbort.current?.abort();
    upcomingAbort.current = new AbortController();
    try {
      const res = await getAllEvents();
      if (upcomingAbort.current.signal.aborted) return;

      const list =
        res?.data?.data ||
        res?.data ||
        res?.events ||
        (Array.isArray(res) ? res : []);

      const normalized = list
        .map(normalizeUpcomingEvent)
        .filter((x) => x.id && x.start_time)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

      setUpcoming(normalized);
    } catch (err) {
      if (err?.name !== "AbortError") setUpcoming([]);
    }
  }, []); // getAllEvents is a stable import — no deps needed

  useEffect(() => {
    loadUpcoming();
    return () => upcomingAbort.current?.abort();
  }, [loadUpcoming]);

  // ---------------------------------------------------------------------------
  // FETCH: event + session — fires when eventId / sessionFromUrl change
  // ---------------------------------------------------------------------------
  const loadEvent = useCallback(async () => {
    if (!eventId) {
      setEvent(null);
      setEventRaw(null);
      setSessionId(null);
      return;
    }

    eventAbort.current?.abort();
    eventAbort.current = new AbortController();

    try {
      const res = await getEventById(eventId);
      if (eventAbort.current.signal.aborted) return;

      if (res?.success && res.data) {
        const d = res.data;
        setEventRaw(d);
        setEvent({
          id: d._id,
          title: d.eventName,
          timezone: d.timezone || "(GMT+0)",
          description: stripHtml(d.description),
        });

        const live = d.sessions?.find((s) => s.meetingStatus === "live");
        const chosen =
          sessionFromUrl || live?._id || d.sessions?.[0]?._id || null;
        setSessionId(chosen);
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Failed to load event details.");
      }
    }
  }, [eventId, sessionFromUrl]);

  useEffect(() => {
    loadEvent();
    return () => eventAbort.current?.abort();
  }, [loadEvent]);

  // ---------------------------------------------------------------------------
  // FETCH: tables — fires for global lobby OR when sessionId is resolved
  //
  // KEY FIX: Previously `loadLobby` depended on `sessionId` which was set
  // asynchronously by `loadEvent`. This created a waterfall:
  //   mount → loadEvent sets sessionId → loadLobby fires
  //
  // Now both effects are coordinated: the lobby effect only starts when
  // `sessionId` is available (for event lobbies) OR immediately (global).
  // ---------------------------------------------------------------------------
  const loadLobby = useCallback(async () => {
    lobbyAbort.current?.abort();
    lobbyAbort.current = new AbortController();
    setLoadingTables(true);

    try {
      // CASE 1 — Global lobby (no eventId in URL)
      if (!eventId) {
        setIsGlobal(true);
        const res = await getAllEventSessionsTables();
        if (lobbyAbort.current.signal.aborted) return;

        if (res?.success && res.data) {
          const flat = flattenGlobalTables(res.data);
          setTables(flat);
          setJoinedId(flat.find((t) => t.isUserInTable)?.id ?? null);
        }
        return;
      }

      // CASE 2 — Event-specific lobby; wait until sessionId is resolved
      setIsGlobal(false);
      if (!sessionId) return; // loadEvent hasn't finished yet — will re-fire

      const res = await getSessionTables(sessionId);
      if (lobbyAbort.current.signal.aborted) return;

      const tablesArr = res?.data?.tables || [];

      // Fetch all participant lists in parallel, tolerating individual failures
      const participantResults = await Promise.allSettled(
        tablesArr.map((t) => getTableParticipants(sessionId, t._id)),
      );
      if (lobbyAbort.current.signal.aborted) return;

      const participantsMap = new Map(
        tablesArr.map((t, idx) => {
          const r = participantResults[idx];
          const list =
            r.status === "fulfilled"
              ? r.value?.data?.participants || r.value?.participants || []
              : [];
          return [t._id, list];
        }),
      );

      const normalized = tablesArr.map((t) =>
        normalizeTable(t, eventId, sessionId, participantsMap.get(t._id)),
      );

      setTables(normalized);
      setJoinedId(tablesArr.find((t) => t.isUserInTable)?._id ?? null);
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Failed to load tables.");
      }
    } finally {
      if (!lobbyAbort.current?.signal.aborted) setLoadingTables(false);
    }
  }, [eventId, sessionId]); // sessionId dependency is intentional

  // ---------------------------------------------------------------------------
// SILENT POLL — refreshes table + participant data without touching loading
// state, so the UI never flickers or shows skeletons during a background sync
// ---------------------------------------------------------------------------
const silentPoll = useCallback(async () => {
  // Skip if a poll is already in-flight — prevents request pile-up
  if (isPollRunning.current) return;
  isPollRunning.current = true;

  try {
    // ── CASE 1: Global lobby ──────────────────────────────────────────────
    if (!eventId) {
      const res = await getAllEventSessionsTables();
      if (!res?.success || !Array.isArray(res.data)) return;

      const flat = flattenGlobalTables(res.data);

      setTables((prev) => {
        // Deep-enough signature: id + taken + seats + attendee count
        const sig = (arr) =>
          arr
            .map((t) => `${t.id}:${t.taken}:${t.seats}:${(t.attendees || []).length}`)
            .join("|");
        return sig(prev) === sig(flat) ? prev : flat;
      });

      // Only update joinedId if the user actually moved tables
      setJoinedId((prev) => {
        const next = flat.find((t) => t.isUserInTable)?.id ?? null;
        return next === prev ? prev : next;
      });

      return;
    }

    // ── CASE 2: Event-specific lobby ──────────────────────────────────────
    if (!sessionId) return;

    const res = await getSessionTables(sessionId);
    const tablesArr = res?.data?.tables || [];
    if (tablesArr.length === 0) return;

    const participantResults = await Promise.allSettled(
      tablesArr.map((t) => getTableParticipants(sessionId, t._id)),
    );

    const participantsMap = new Map(
      tablesArr.map((t, idx) => {
        const r = participantResults[idx];
        const list =
          r.status === "fulfilled"
            ? r.value?.data?.participants || r.value?.participants || []
            : [];
        return [t._id, list];
      }),
    );

    const normalized = tablesArr.map((t) =>
      normalizeTable(t, eventId, sessionId, participantsMap.get(t._id)),
    );

    setTables((prev) => {
      const sig = (arr) =>
        arr
          .map((t) => `${t.id}:${t.taken}:${t.seats}:${(t.attendees || []).length}`)
          .join("|");
      return sig(prev) === sig(normalized) ? prev : normalized;
    });

    setJoinedId((prev) => {
      const next = tablesArr.find((t) => t.isUserInTable)?._id ?? null;
      return next === prev ? prev : next;
    });

  } catch {
    // Intentionally silent — never surface poll errors to the user
  } finally {
    isPollRunning.current = false;
  }
}, [eventId, sessionId]);

  useEffect(() => {
    loadLobby();
    return () => lobbyAbort.current?.abort();
  }, [loadLobby]);

  // ---------------------------------------------------------------------------
// BACKGROUND POLL — starts after initial load, clears on unmount / dep change
// Only runs when tables have loaded at least once (loadingTables === false)
// ---------------------------------------------------------------------------
useEffect(() => {
  // Wait until the initial load has settled before starting the poll.
  // We check via a one-time flag rather than the loadingTables state
  // so the interval is NOT restarted every time loadingTables toggles.
  clearInterval(pollRef.current);

  pollRef.current = setInterval(silentPoll, POLL_INTERVAL_MS);

  return () => clearInterval(pollRef.current);
}, [silentPoll]); // silentPoll already depends on eventId + sessionId


useEffect(() => {
  return () => clearInterval(pollRef.current);
}, []);
  // ---------------------------------------------------------------------------
  // STABLE ACTION HANDLERS
  // ---------------------------------------------------------------------------

  const handleJoin = useCallback(
    async (t) => {
      if (!user) {
        navigate(
          `/login?next=${encodeURIComponent(
            window.location.pathname + window.location.search,
          )}`,
        );
        return;
      }

      const targetSessionId = t.session_id || sessionId;

      if (!isGlobal && !isBooked) {
        setRegisterOpen(true);
        return;
      }

      try {
        await joinSessionTable(targetSessionId, t.id, {});
        toast.success(`You're seated at "${t.topic}".`);
        silentPoll();
      } catch {
        toast.error(
          "Could not join table. Ensure you are registered for this event.",
        );
      }
    },
    [user, sessionId, isGlobal, isBooked, navigate, silentPoll],
  );

  const handleSwitch = useCallback(async () => {
    const targetId = joinedTable?.session_id || sessionId;
    try {
      await leaveSessionTable(targetId);
      setJoinedId(null);
      toast("You've left the table.");
      silentPoll();
    } catch {
      setJoinedId(null);
    }
  }, [joinedTable, sessionId, silentPoll]);

  const handleAttendeeClick = useCallback((name) => setProfileFor(name), []);
  const handleViewAgenda = useCallback((t) => setAgendaTable(t), []);
  const handleCloseAgenda = useCallback(() => setAgendaTable(null), []);
  const handleCloseProfile = useCallback(() => setProfileFor(null), []);
  const handleOpenRegister = useCallback(() => setRegisterOpen(true), []);
  const handleCloseRegister = useCallback(() => setRegisterOpen(false), []);
  const handleCircle = useCallback((f) => setActiveCircle(f), []);
  const handleToggleOpen = useCallback((val) => setOnlyOpen(val), []);

  const handleBooked = useCallback(
    (ticket) => {
      // After registration, reload both event (for isBooked flag) and tables
      loadEvent();
      silentPoll();
    },
    [loadEvent, silentPoll],
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen">
      <LobbyHero
        isGlobal={isGlobal}
        tables={tables}
        filters={filters}
        activeCircle={activeCircle}
        onCircle={handleCircle}
        onlyOpen={onlyOpen}
        onToggleOpen={handleToggleOpen}
        upcoming={upcoming}
      />

      <JoinedBanner joinedTable={joinedTable} onLeave={handleSwitch} />

      <section className="section">
        <div className="container-x">
          <TableGrid
            tables={visible}
            loading={loadingTables}
            event={event}
            joinedId={joinedId}
            onJoin={handleJoin}
            onSwitch={handleSwitch}
            onAttendeeClick={handleAttendeeClick}
            onViewAgenda={handleViewAgenda}
          />
        </div>
      </section>

      {/* Modals — only mount DOM when open */}
      <AttendeeProfileModal
        attendeeId={profileFor}
        onClose={handleCloseProfile}
      />

      <RegisterModal
        open={registerOpen}
        onClose={handleCloseRegister}
        eventId={eventId}
        eventTitle={event?.title}
        user={user}
        onBooked={handleBooked}
      />

      <AgendaModal
        open={!!agendaTable}
        onClose={handleCloseAgenda}
        table={agendaTable}
      />

      {/* WelcomeVideoOverlay — only mount when we have a video to show */}
      {joinedTable?.welcome_video_url && (
        <WelcomeVideoOverlay
          src={joinedTable.welcome_video_url}
          title="Welcome"
          storageKey={`welcome:table:${joinedId}`}
        />
      )}
    </div>
  );
}
